import React, { useState, useEffect } from 'react';
import {
  Users, Facebook, BarChart3, Settings, LogOut, Shield,
  Bot, MessageSquare, Menu, X, Crown, Shirt, ShoppingBag,
  Video, Wand2, FileText, Sparkles
} from 'lucide-react';
import PageManagement from './PageManagement';
import UserManagement from './UserManagement';
import FacebookAppsManagement from './FacebookAppsManagement';
import OfficialPagesManagement from './OfficialPagesManagement';
import BotStudio from './BotStudio';
import CreditOrders from './CreditOrders';
import ManyChatPagesManagement from './ManyChatPagesManagement';
import FashionGlowQuotas from './FashionGlowQuotas';
import AdminSubscriptions from './AdminSubscriptions';
import VideoAdsQuotas from './VideoAdsQuotas';
import MarketingStudioAdmin from './MarketingStudioAdmin';
import CVCreditsAdmin from './CVCreditsAdmin';   // ✅ NOUVEAU
import { API_URL } from '../../config';
import AssistantTeckiAAdmin from './AssistantTeckiAAdmin';


export default function AdminDashboard({ onLogout }) {
  const [view, setView]                           = useState('dashboard');
  const [sidebarOpen, setSidebarOpen]             = useState(false);
  const [pendingSubscriptions, setPendingSubscriptions] = useState(0);
  const [stats, setStats] = useState({
    total_users: 0, active_users: 0,
    total_clients: 0, active_clients: 0,
    inactive_clients: 0, active_sessions: 0,
    monitored_posts: 0, total_comments: 0,
  });

  useEffect(() => {
    loadStats();
    loadPendingSubscriptions();
  }, []);

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadPendingSubscriptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/subscriptions`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPendingSubscriptions(data.pending_setup || 0);
      }
    } catch (error) {
      console.error('Error loading pending subscriptions:', error);
    }
  };

  const handleNavClick = (newView) => {
    setView(newView);
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-red-950 to-slate-950">

      {/* ── Mobile Header ── */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-black/40 backdrop-blur-xl border-b border-white/10 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-white font-bold text-sm">Admin Panel</div>
            <div className="text-xs text-gray-400">TDI Digital</div>
          </div>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 bg-white/10 rounded-lg text-white"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* ── Sidebar ── */}
      <div className={`
        fixed top-0 left-0 h-full w-64 bg-black/40 backdrop-blur-xl border-r border-white/10 p-6 z-50
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        flex flex-col
      `}>
        {/* Logo */}
        <div className="hidden lg:flex items-center gap-3 mb-10">
          <img src="/TeckiA-logo.gif" alt="Admin Panel" className="w-12 h-12 rounded-xl object-cover" />
          <div>
            <div className="text-white font-bold">Admin Panel</div>
            <div className="text-xs text-gray-400">Agent TeckiA</div>
          </div>
        </div>
        <div className="h-4 lg:hidden" />

        {/* Nav */}
        <nav className="space-y-1 flex-1 overflow-y-auto">
          <NavItem icon={BarChart3}    label="Dashboard"            active={view === 'dashboard'}           onClick={() => handleNavClick('dashboard')} />

          {/* ── Utilisateurs ── */}
          <SectionLabel label="Utilisateurs" />
          <NavItem icon={Users}        label="Utilisateurs"          active={view === 'users'}                onClick={() => handleNavClick('users')} />

          {/* ── Facebook ── */}
          <SectionLabel label="Facebook" />
          <NavItem icon={Facebook}     label="Pages API Officielle"  active={view === 'official_pages'}       onClick={() => handleNavClick('official_pages')} badge="NEW" badgeColor="green" />
          <NavItem icon={Facebook}     label="Pages Facebook"        active={view === 'pages'}                onClick={() => handleNavClick('pages')} />
          <NavItem icon={Settings}     label="Apps Facebook"         active={view === 'apps'}                 onClick={() => handleNavClick('apps')} />
          <NavItem icon={Bot}          label="Bot Studio"            active={view === 'bot_studio'}           onClick={() => handleNavClick('bot_studio')} badge="NEW" badgeColor="green" />
          <NavItem icon={MessageSquare} label="ManyChat Pages"       active={view === 'manychat'}             onClick={() => handleNavClick('manychat')} />
          <NavItem icon={Sparkles} label="TeckiA Assistant" active={view==='assistant_admin'} onClick={()=>handleNavClick('assistant_admin')} badge="IA" badgeColor="green"/>

          {/* ── Abonnements & Crédits ── */}
          <SectionLabel label="Abonnements & Crédits" />
          <NavItem
            icon={ShoppingBag}
            label="Abonnements"
            active={view === 'admin_subscriptions'}
            onClick={() => handleNavClick('admin_subscriptions')}
            badge={pendingSubscriptions > 0 ? pendingSubscriptions : null}
            badgeColor="yellow"
          />
          <NavItem
            icon={ShoppingBag}
            label="Commandes Crédits"
            active={view === 'credit_orders'}
            onClick={() => handleNavClick('credit_orders')}
            badge="AUTO"
            badgeColor="green"
          />
          <NavItem
            icon={FileText}
            label="Crédits CV IA"
            active={view === 'cv_credits'}
            onClick={() => handleNavClick('cv_credits')}
            badge="CV"
            badgeColor="violet"
          />

          {/* ── Quotas services ── */}
          <SectionLabel label="Quotas Services" />
          <NavItem icon={Video}        label="Video Ads Quotas"      active={view === 'video_ads_quotas'}     onClick={() => handleNavClick('video_ads_quotas')}     badge="PRO" badgeColor="green" />
          <NavItem icon={Wand2}        label="Marketing Studio"      active={view === 'marketing_studio_admin'} onClick={() => handleNavClick('marketing_studio_admin')} badge="PRO" badgeColor="green" />
          <NavItem icon={Shirt}        label="Marketing Quotas"      active={view === 'fashionglow'}          onClick={() => handleNavClick('fashionglow')} />
        </nav>

        {/* Déconnexion */}
        <button
          onClick={onLogout}
          className="mt-6 flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-all w-full"
        >
          <LogOut className="w-4 h-4" />
          <span className="text-sm">Déconnexion</span>
        </button>
      </div>

      {/* Overlay mobile */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Main Content ── */}
      <div className={`lg:ml-64 pt-16 lg:pt-0 ${['users','video_ads_quotas','marketing_studio_admin'].includes(view) ? 'p-0 overflow-hidden' : 'p-4 md:p-6 lg:p-8'}`}>

        {/* ─── DASHBOARD ──────────────────────────────────────────────────── */}
        {view === 'dashboard' && (
          <div className="space-y-6 md:space-y-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2">Dashboard Admin</h1>
              <p className="text-sm md:text-base text-blue-200">Vue d'ensemble du système</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              <StatCard title="Utilisateurs"    value={stats.total_users}    subtitle={`${stats.active_users} actifs`}          icon={Users}    color="from-blue-600 to-cyan-600" />
              <StatCard title="Pages Facebook"  value={stats.total_clients}  subtitle={`${stats.active_clients} actives`}        icon={Facebook} color="from-purple-600 to-pink-600" />
              <StatCard title="Commentaires"    value={stats.total_comments} subtitle="Total système"                            icon={BarChart3} color="from-green-600 to-emerald-600" />
            </div>

            {/* Alerte abonnements en attente */}
            {pendingSubscriptions > 0 && (
              <div className="bg-gradient-to-br from-yellow-600 to-orange-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white animate-pulse">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                  <ShoppingBag className="w-10 h-10 md:w-12 md:h-12" />
                  <div className="flex-1">
                    <h3 className="text-xl md:text-2xl font-bold mb-1">
                      {pendingSubscriptions} Abonnement{pendingSubscriptions > 1 ? 's' : ''} en attente
                    </h3>
                    <p className="text-sm md:text-base text-white/80">Configuration requise pour les nouveaux services</p>
                  </div>
                </div>
                <button onClick={() => handleNavClick('admin_subscriptions')} className="w-full sm:w-auto px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 font-medium transition-all">
                  Voir les abonnements →
                </button>
              </div>
            )}

            {/* ── Card CV Crédits ── */}
            <div className="bg-gradient-to-br from-violet-700 to-blue-700 rounded-xl md:rounded-2xl p-4 md:p-6 text-white">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <FileText className="w-10 h-10 md:w-12 md:h-12" />
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold mb-1">Crédits CV IA</h3>
                  <p className="text-sm md:text-base text-white/80">
                    Gérez les crédits de génération de CV par utilisateur · 1 crédit = 1 500 Ar
                  </p>
                </div>
              </div>
              <button onClick={() => handleNavClick('cv_credits')} className="w-full sm:w-auto px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 font-medium transition-all">
                Gérer les crédits CV →
              </button>
            </div>

            {/* ── Card Video Ads ── */}
            <div className="bg-gradient-to-br from-pink-600 to-orange-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <Video className="w-10 h-10 md:w-12 md:h-12" />
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold mb-1">TeckiA Video Ads Quotas</h3>
                  <p className="text-sm md:text-base text-white/80">Gérez les quotas vidéo Standard PRO & Grok Imagine par utilisateur</p>
                </div>
              </div>
              <button onClick={() => handleNavClick('video_ads_quotas')} className="w-full sm:w-auto px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 font-medium transition-all">
                Gérer les quotas →
              </button>
            </div>

            {/* ── Card ManyChat ── */}
            <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <MessageSquare className="w-10 h-10 md:w-12 md:h-12" />
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold mb-1">ManyChat Bot Multi-Webhook</h3>
                  <p className="text-sm md:text-base text-white/80">Gérez vos bots ManyChat avec webhook unique par page</p>
                </div>
              </div>
              <button onClick={() => handleNavClick('manychat')} className="w-full sm:w-auto px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 font-medium transition-all">
                Gérer ManyChat →
              </button>
            </div>

            {/* ── Card Messenger ── */}
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <Bot className="w-10 h-10 md:w-12 md:h-12" />
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold mb-1">Bot Studio</h3>
                  <p className="text-sm md:text-base text-white/80">Configurez les agents IA de vos clients — identité, produits, documents OCR, ventes</p>
                </div>
              </div>
              <button onClick={() => handleNavClick('bot_studio')} className="w-full sm:w-auto px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 font-medium transition-all">
                Ouvrir Bot Studio →
              </button>
            </div>
          </div>
        )}

        {/* ─── VUES ──────────────────────────────────────────────────────── */}
        {view === 'users'                   && <UserManagement />}
        {view === 'pages'                   && <PageManagement />}
        {view === 'official_pages'          && <OfficialPagesManagement />}
        {view === 'bot_studio'              && <BotStudio />}
        {view === 'credit_orders'           && <CreditOrders />}
        {view === 'apps'                    && <FacebookAppsManagement />}
        {view === 'manychat'                && <ManyChatPagesManagement />}
        {view === 'fashionglow'             && <FashionGlowQuotas />}
        {view === 'admin_subscriptions'     && <AdminSubscriptions />}
        {view === 'video_ads_quotas'        && <VideoAdsQuotas />}
        {view === 'marketing_studio_admin'  && <MarketingStudioAdmin />}
        {view === 'assistant_admin' && <AssistantTeckiAAdmin />}
        {view === 'cv_credits'              && <CVCreditsAdmin />}   {/* ✅ NOUVEAU */}
      </div>
    </div>
  );
}

// ── Composants helpers ────────────────────────────────────────────────────────

const SectionLabel = ({ label }) => (
  <div className="px-4 pt-4 pb-1">
    <span className="text-gray-500 text-xs font-semibold uppercase tracking-wider">{label}</span>
  </div>
);

const NavItem = ({ icon: Icon, label, active, onClick, badge, badgeColor = 'green' }) => {
  const badgeColors = {
    green:  'bg-green-500',
    yellow: 'bg-yellow-500',
    red:    'bg-red-500',
    violet: 'bg-violet-500',
  };
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all relative text-left ${
        active ? 'bg-white/10 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'
      }`}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      <span className="font-medium text-sm truncate">{label}</span>
      {badge && (
        <span className={`absolute right-2 top-1/2 -translate-y-1/2 px-2 py-0.5 ${badgeColors[badgeColor]} text-white text-xs font-bold rounded-full ${
          typeof badge === 'number' && badge > 0 ? 'animate-pulse' : ''
        }`}>
          {badge}
        </span>
      )}
    </button>
  );
};

const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
  <div className={`bg-gradient-to-br ${color} rounded-xl md:rounded-2xl p-4 md:p-6 text-white`}>
    <div className="flex items-center justify-between mb-3 md:mb-4">
      <Icon className="w-8 h-8 md:w-10 md:h-10 opacity-80" />
      <div className="text-3xl md:text-4xl font-bold">{value}</div>
    </div>
    <h3 className="text-base md:text-lg font-semibold mb-1">{title}</h3>
    <p className="text-white/80 text-xs md:text-sm">{subtitle}</p>
  </div>
);